/**
 * The Search Progress tab — the three charts the legacy nuAdvisory scoreboard
 * shows, minus the two bugs it shows them with, plus a date range and a
 * week / month switch.
 *
 * Reference: `nu-workspace-ai/D&D/meetings/2026-08-18/artifacts/search-progress-tab-nu-advisory-charts.png`.
 *
 * TWO DELIBERATE DIVERGENCES FROM THE LEGACY SCREEN, both of them corrections
 * rather than redesigns, and both worth knowing about before comparing the two
 * side by side:
 *
 * 1. The legacy "Reference" line is not Reference. `ScoreboardService.cs` (~line
 *    156) assigns `ReferenceQty = rejected`, so the series labelled Reference has
 *    been plotting the REJECTED count. On search 3378 that is 483 against
 *    Reference's actual 3. Here the Reference line is Reference — every line is
 *    keyed by the stage name Thrive recorded, and there is no per-stage
 *    assignment anywhere for that class of mistake to live in.
 * 2. The legacy "Totals" line adds stage transitions to outreaches. Those are
 *    different units — a candidacy moving and a message sent — so no chart here
 *    sums them. Outreaches keep their own panel and their own axis.
 *
 * A third divergence is a scope choice rather than a correction: the legacy
 * legend is a hardcoded seven, and this renders every stage the search actually
 * has. Live history for 3378 has eleven, including Source (150 entries) and
 * Client Interview (27) — both absent from the old chart, both with real volume.
 * The per-line toggles are what keep that from being clutter, and since
 * 2026-08-25 the off-ladder ones OPEN toggled off — plus `Source`, which is a
 * ladder rung and is hidden anyway as of 2026-08-27 because Jared ruled its
 * holders are not candidates. That exception is why the rule can no longer be
 * read off the ladder alone; `charts/seriesDefaults.ts` carries it, the reason,
 * and the three errors in the list Jared dictated from memory.
 *
 * THE RANGE AND THE GRANULARITY LIVE IN THE URL, the series toggles do not, and
 * the split is the point. A date range is the thing worth pasting into Slack —
 * "look at what happened the week of the 26th" — and a Back press should undo
 * it. A half-hidden legend is not a view anyone links to, and putting it in the
 * query string would compete for the same URL with the thing that is.
 *
 * NOTHING HERE FETCHES. All three charts, at every granularity and every range,
 * are arithmetic over `stage_transitions_by_day` and `outreach_activity` — two
 * fields the cached report already carries. A round-trip per granularity click
 * would add latency and an endpoint for a sum the browser can do on data in its
 * hands, and it would let the two tabs show figures composed at different
 * moments. The one control here that DOES reach the server is Refresh, and it
 * throws the cached report away rather than re-reading it — for everyone, since
 * one search has one cached report.
 *
 * The rollup itself lives in `charts/timeRange.ts`, unit-tested and mock-free —
 * including the trap this tab is most exposed to, which is a filtered cumulative
 * that forgets its baseline. See that file's header.
 *
 * WHY THE PROSE UNDER EACH HEADER IS GONE. Jared spent most of the 08-25 call on
 * this tab and the through-line was space: *"there's so much empty space here and
 * so much empty space here… way too much wasted space."* Three subtitles, three
 * per-chart footnotes and a right-aligned control row that left the top-left
 * corner empty. The subtitles moved into info tooltips on the headers, the
 * footnotes went, and the controls moved to the top left with the first chart
 * beside them. What each sentence SAYS is unchanged — chart 2's in particular is
 * carried across verbatim, because he dictated it.
 *
 * Design-system token utilities only; no inline `style`; English copy only.
 */

import type { SearchReport } from "@nu-assist/contracts";
import { Button } from "@nu-assist/ui";
import { RefreshCw } from "lucide-react";
import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import { ReportPanel } from "./ReportPanel";
import { OutreachLines } from "./charts/OutreachLines";
import { StageLines } from "./charts/StageLines";
import { titleCaseNoun } from "./charts/chartTitles";
import { dayLabel, isDayKey } from "./charts/dayAxis";
import { viewFor } from "./charts/progressView";
import {
  GRANULARITIES,
  type Granularity,
  RANGE_SHORTCUTS,
  type RangeShortcut,
  activeShortcutId,
  shortcutStart,
} from "./charts/timeRange";

export interface SearchProgressPanelProps {
  report: SearchReport;
  /**
   * Discards the cached report so the next read rebuilds it from Thrive.
   *
   * Optional because the panel renders in contexts with no mutation to hand
   * (its own unit tests, and any future read-only embed). Absent, the note at
   * the foot still says when the report was generated — it just does not offer
   * the button, which is better than offering one that cannot act.
   */
  onRefresh?: () => void;
  /** True while a rebuild is in flight; the button says so and refuses a second press. */
  refreshing?: boolean;
}

const NO_STAGE_HISTORY = "No candidate has moved between stages on this search yet.";
const NO_OUTREACH = "No outreach recorded on this search.";

/**
 * The sentence Jared dictated for the second chart, on the 08-25 call, in his
 * words: *"This would be the tool tip — counts every candidate who ever entered
 * that stage, including those who have since moved on."*
 *
 * Hoisted to a constant rather than left inline because it MOVED — it was the
 * panel's subtitle before it was the panel's tooltip — and the risk a move
 * carries is a retype. It is quoted, not paraphrased, and it is also the
 * disambiguation the header now needs: "Total In Each Stage" reads as "how many
 * are in this stage now", which is what the Dashboard donut answers with very
 * different numbers (on search 4379, Identified 368 here against 3 there). The
 * longer title used to carry that distinction; this sentence carries it now.
 */
const TOTAL_IN_STAGE_TOOLTIP =
  "Counts every candidate who ever entered the stage, including those who have since moved on";

/** How the granularity switch labels itself. */
const GRANULARITY_LABEL: Record<Granularity, string> = {
  week: "Week",
  month: "Month",
};

/**
 * The quick-range chip, shared by `Full range` and the three shortcuts so one
 * "selected" look serves the whole set.
 *
 * NOT shared with the granularity switch below it, deliberately: that one is a
 * joined segmented control (`first:rounded-l-sm`, no gaps) because its options
 * are mutually exclusive views of the SAME data, where the quick ranges are
 * separate jumps. Two affordances that look alike but behave alike in different
 * ways would be worse than two that look different.
 */
function chipClass(active: boolean): string {
  return `rounded-sm border px-2 py-1 text-caption focus:outline-none focus:ring-1 focus:ring-accent ${
    active
      ? "border-accent bg-accent-muted text-accent-ink"
      : "border-border text-text-secondary hover:bg-surface-raised"
  }`;
}

/** The shared frame for the two control groups, so they read as one column. */
// ONE ROW PER GROUP, not a stacked card. Each group used to be a `flex-col`
// box — label on its own line, controls under it — so the two of them together
// cost three rows of vertical space above a chart that is the reason anyone
// opened the tab. Jose, 2026-09-01: *"el filtro de tiempo en search progress
// debería ocupar menos espacio, idealmente una línea."*
//
// `items-center` rather than `items-end`: with the labels now inline there is
// no baseline to align to, and the date inputs are the tallest thing in the row.
const CONTROL_GROUP =
  "flex flex-wrap items-center gap-x-3 gap-y-2 rounded-md border border-border bg-surface px-3 py-2";

export function SearchProgressPanel({
  report,
  onRefresh,
  refreshing = false,
}: SearchProgressPanelProps) {
  const [searchParams, setSearchParams] = useSearchParams();

  // The whole computation lives in `viewFor`, shared with the report export.
  // It used to be inline here, and the export re-derived it — which is how a
  // downloaded document came to ignore `?from`, `?to` and `?bucket` entirely
  // and cover the year while the screen showed April. One implementation, two
  // readers.
  const view = useMemo(
    () =>
      viewFor(report, {
        from: searchParams.get("from"),
        to: searchParams.get("to"),
        bucket: searchParams.get("bucket"),
      }),
    [report, searchParams]
  );
  const { bounds, granularity, filtered } = view;
  const range = view.range;

  const pointLabel = view.pointLabel;
  const pointNoun = granularity;
  // Capitalized for the TITLES only -- `pointNoun` keeps feeding the tooltips,
  // the axis announcements and the chart components' own aria sentences, which
  // are prose. The rule and its history live in `charts/chartTitles.ts`.
  const pointNounTitle = titleCaseNoun(pointNoun);

  // FR3.6: an empty result NAMES the range that produced it. "No candidate has
  // moved between stages on this search yet" over a filtered view would be a
  // false claim about the search rather than a true one about the filter.
  const rangeSuffix =
    filtered && range ? ` between ${dayLabel(range.from)} and ${dayLabel(range.to)}` : "";
  const noStages = filtered ? `No candidate moved between stages${rangeSuffix}.` : NO_STAGE_HISTORY;
  const noOutreach = filtered ? `No outreach was logged${rangeSuffix}.` : NO_OUTREACH;

  function updateParams(mutate: (next: URLSearchParams) => void) {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      mutate(next);
      return next;
    });
  }

  /**
   * A chip writes the SAME `?from=`/`?to=`/`?bucket=` state the pickers write.
   *
   * It is a faster way to set the range, never a fourth source of truth: there
   * is no "which chip is selected" in component state, so a reload, a shared
   * link and a hand-typed date that lands on the same span all light the same
   * chip. `activeShortcutId` recovers it from the resolved range.
   */
  function applyShortcut(shortcut: RangeShortcut) {
    if (!bounds) return;
    const start = shortcutStart(shortcut, bounds.last);
    // Clamp rather than pad: "Last 1 year" on a three-month search is those
    // three months, not nine empty leading ones.
    const from = start < bounds.first ? bounds.first : start;
    updateParams((next) => {
      if (from === bounds.first) next.delete("from");
      else next.set("from", from);
      // Every shortcut ends at the anchor, which IS `bounds.last`, and the
      // canonical spelling of that is the absence of the parameter.
      next.delete("to");
      // `week` is the default granularity since `day` left the switch, so it is
      // the ABSENCE of the parameter rather than a second spelling of it.
      if (shortcut.bucket === "week") next.delete("bucket");
      else next.set("bucket", shortcut.bucket);
    });
  }

  const activeShortcut =
    range && bounds ? activeShortcutId(range.from, range.to, granularity, bounds) : null;

  // Pre-window activity is how this tab KNOWS the 366-day cap is biting: the
  // report only ships a baseline when there were transitions it could not plot.
  // Read off the UNFILTERED progress, not `view.daily` — the latter grows with
  // whatever the reader just filtered out, which is a different fact.
  //
  // THIS IS ALSO WHY THE FULL-RANGE DEFAULT IS NOT A SILENT LIE. The tab opens on
  // the whole span with no parameters in the URL, and on a search older than a
  // year "the whole span" is the last 366 days of it and no more. The notice
  // below fires on the report's own baseline rather than on anything the reader
  // did, so it is on screen from the first paint — the default and the caveat
  // arrive together, which is the only arrangement in which the default is
  // honest. A search that fits inside the window has nothing to explain and
  // gets no notice.
  const cappedBefore = view.unfilteredCarriedIn + (report.outreach_outside_window_count ?? 0) > 0;

  // `generated_at` is a full ISO instant; the axis formatter takes a day key.
  // Same UTC formatter every date on this tab goes through, rather than a
  // second `toLocaleDateString` that would render a boundary differently from
  // the chart ticks beside it.
  const generatedDay = (report.generated_at ?? "").slice(0, 10);
  const generatedLabel = isDayKey(generatedDay) ? dayLabel(generatedDay) : null;

  // Built here rather than inline among its two siblings because it is rendered
  // from two branches: under the controls when there is a range to control, and
  // on its own when there is not. It is the same chart, at the same width, in
  // both — see the layout note below for why that width is now the point.
  const newInStageChart = (
    <ReportPanel
      // The title still follows the granularity. A panel headed "by day" over
      // weekly buckets is the kind of small lie that makes a reader distrust the
      // numbers under it.
      //
      // KEPT AS IT WAS, and that is a flag rather than a decision. Jared asked
      // for a new header and a new tooltip for this chart and said he would
      // email both — *"this should be the header and I can just email this to
      // you with this is the tool tip"*. As of 2026-08-27 no email has arrived,
      // so the existing prose moves into the tooltip unchanged and the header
      // stands. Both are one string edit away when it does.
      title={`New Candidates In Each Stage By ${pointNounTitle}`}
      tooltip={`Candidacies that entered each stage that ${pointNoun}`}
      testId="stage-new"
    >
      <StageLines
        days={view.daily.days}
        series={view.daily.series}
        emptyMessage={noStages}
        testId="stage-new-lines"
        label={`New Candidates In Each Stage By ${pointNounTitle}`}
        pointLabel={pointLabel}
        pointNoun={pointNoun}
        showSummary={false}
      />
    </ReportPanel>
  );

  return (
    <div data-testid="search-detail-progress" className="flex flex-col gap-4">
      {bounds ? (
        // CONTROLS ABOVE, HORIZONTAL, AND ALL THREE CHARTS THE SAME WIDTH.
        //
        // THIS REVERSES THE TWO-COLUMN LAYOUT SHIPPED FOR #729 cl.3 four days
        // earlier, and the reversal is not a regression — it is Jared seeing the
        // built thing for the first time. The previous note read: *"the range
        // probably should go up here with the group eye right below it here"*,
        // and that is exactly what was built. Then, 2026-08-27, looking at it:
        // *"then put it above and make it a little more horizontal, so it seems
        // like it's paired with that one. It also looks a little bit funny where
        // the top table is narrower than the rest. So make all three tables look
        // the same and just move those two things above that chart."*
        //
        // WHAT THE OLD LAYOUT GOT WRONG WAS NOT SPACING, IT WAS SCOPE. Jared
        // arrived at it by asking a question: *"does that left button on the
        // range, does that affect all three tables or just that top one?"* The
        // range governs all three charts on this tab. Sitting in a column beside
        // the first one, it read as governing only the first one — the layout
        // was answering a question the reader had not asked and getting it
        // wrong. Above all three, spanning the full width, it reads as what it
        // is.
        //
        // The narrowness he noticed was structural, not a style choice: the grid
        // gave chart 1 `1fr` of a row it shared with an 18rem column while its
        // two siblings below were full width. Dropping the grid fixes the scope
        // signal and the width mismatch with one change.
        //
        // NOT DONE, AND NOT SILENTLY DROPPED: presets inside the date picker
        // popup (Jose's AWS-style idea, #729 cl.3, marked EXPLORATORY). It is
        // unreachable from `<input type="date">` — see the note on the range
        // fieldset below — and Jared asked for this instead on 08-27, so it is
        // superseded rather than pending.
        <div className="flex flex-col gap-3">
          {/* `flex-wrap` rather than a fixed track count: the two fieldsets are
              different widths and the row has to survive a narrow viewport
              without either of them being crushed. They stack there, which is
              what they did below `lg:` before this change too. */}
          <div data-testid="progress-controls" className="flex flex-wrap items-start gap-3">
            {/* THE QUICK RANGES AND THE PICKERS IN ONE BOX. They were two
                separate control groups sitting side by side; they set the same
                two URL parameters, so a reader who pressed a chip and then
                nudged a date was operating one control in two places.

                This is as far as Jose's AWS-style idea reaches without a
                redesign. The literal version — presets INSIDE the calendar
                popup — is not reachable from `<input type="date">`: the popup is
                the browser's own UI and carries no extension point. Building it
                means a bespoke calendar, which the note below says is a
                design-system decision bigger than this tab. Same box, not same
                popup; recorded as not-done rather than quietly dropped. */}
            <fieldset className={CONTROL_GROUP}>
              <legend className="sr-only">Date range</legend>
              <span aria-hidden="true" className="text-caption text-text-secondary">
                Range
              </span>
              <div className="flex flex-wrap items-center gap-1">
                {/* `Full range` belongs with the shortcuts rather than beside
                    the pickers: it is the "no shortcut" member of the same set,
                    and a reader looking for the way back should find it where
                    they found the way in. It is always rendered, not only once
                    something is filtered, so the group reads as a set of choices
                    with one of them current instead of a row that grows a
                    button. */}
                <button
                  type="button"
                  aria-pressed={!filtered}
                  data-testid="progress-reset-range"
                  onClick={() =>
                    updateParams((next) => {
                      // The full range is the ABSENCE of the parameters, so this
                      // deletes rather than sets — the canonical URL for the tab
                      // stays the bare one.
                      next.delete("from");
                      next.delete("to");
                    })
                  }
                  className={chipClass(!filtered)}
                >
                  Full range
                </button>
                {RANGE_SHORTCUTS.map((shortcut) => (
                  <button
                    key={shortcut.id}
                    type="button"
                    aria-pressed={activeShortcut === shortcut.id}
                    data-testid={`progress-shortcut-${shortcut.id}`}
                    onClick={() => applyShortcut(shortcut)}
                    className={chipClass(activeShortcut === shortcut.id)}
                  >
                    {shortcut.label}
                  </button>
                ))}
              </div>

              {/* NATIVE `<input type="date">`, and that choice is deliberately
                  the smallest one available. `@nu-assist/ui` has no date-picker
                  primitive and the repo has no date library, so the alternatives
                  were a bespoke calendar popup — a design-system decision far
                  bigger than this tab should take alone — or the browser's own,
                  which arrives accessible, localized and keyboard-operable, and
                  whose `min`/`max` bound the range to what the data can answer
                  (FR3.8) without any UI of our own. When a `DateField` primitive
                  lands, these two call sites are what changes, and the AWS-style
                  presets-in-the-calendar idea becomes buildable with it. */}
              <div className="flex flex-wrap items-center gap-2">
                {/* Labels sit BESIDE their input now, not above it. Two stacked
                    label+input pairs were the second of the three rows this
                    group used to cost. */}
                <label className="flex items-center gap-1.5 text-caption text-text-secondary">
                  From
                  <input
                    type="date"
                    data-testid="progress-from"
                    value={range?.from ?? ""}
                    min={bounds.first}
                    max={bounds.last}
                    onChange={(event) =>
                      updateParams((next) => {
                        if (event.target.value) next.set("from", event.target.value);
                        else next.delete("from");
                      })
                    }
                    className="rounded-sm border border-border bg-surface px-2 py-1 text-body text-text focus:outline-none focus:ring-1 focus:ring-accent"
                  />
                </label>
                <label className="flex items-center gap-1.5 text-caption text-text-secondary">
                  To
                  <input
                    type="date"
                    data-testid="progress-to"
                    value={range?.to ?? ""}
                    min={bounds.first}
                    max={bounds.last}
                    onChange={(event) =>
                      updateParams((next) => {
                        if (event.target.value) next.set("to", event.target.value);
                        else next.delete("to");
                      })
                    }
                    className="rounded-sm border border-border bg-surface px-2 py-1 text-body text-text focus:outline-none focus:ring-1 focus:ring-accent"
                  />
                </label>
              </div>
            </fieldset>

            <fieldset className={CONTROL_GROUP}>
              <legend className="sr-only">Chart granularity</legend>
              <span aria-hidden="true" className="text-caption text-text-secondary">
                Group by
              </span>
              <div className="flex rounded-sm border border-border">
                {GRANULARITIES.map((option) => (
                  <button
                    key={option}
                    type="button"
                    aria-pressed={granularity === option}
                    data-testid={`progress-bucket-${option}`}
                    onClick={() =>
                      updateParams((next) => {
                        // `week` is the default, so it is the absence of the
                        // parameter rather than a second spelling of it. It took
                        // that role from `day` when the switch dropped to two
                        // options; `?bucket=day` in an older link now falls back
                        // to it like any other unreadable value.
                        if (option === "week") next.delete("bucket");
                        else next.set("bucket", option);
                      })
                    }
                    className={`flex-1 px-3 py-1 text-caption first:rounded-l-sm last:rounded-r-sm focus:outline-none focus:ring-1 focus:ring-accent ${
                      granularity === option
                        ? "bg-accent-muted text-accent-ink"
                        : "text-text-secondary hover:bg-surface-raised"
                    }`}
                  >
                    {GRANULARITY_LABEL[option]}
                  </button>
                ))}
              </div>
            </fieldset>
          </div>

          {/* Siblings of the controls now, not a column beside them, so every
              notice spans the same full width as the charts. No fragment
              wrapping them: they are already direct children of this block, and
              the `<>…</>` that was here was leftover scaffolding from the
              removed column `div`. */}
          {/* FR3.7: a range we could not honour says so where the reader is
                looking, rather than quietly showing them a different one. An
                inverted range is REJECTED and the full range drawn — never
                swapped, because `from` after `to` is a typo and guessing which
                end was meant answers a question nobody asked. */}
          {range?.notice ? (
            <p
              data-testid="progress-range-notice"
              className="rounded-md border border-warning-strong bg-warning-muted px-3 py-2 text-caption text-warning-ink"
            >
              {range.notice}
            </p>
          ) : null}

          {/* FR3.9(b): why there is no "Last 2 years" chip, and the caveat the
                full-range default depends on.
                The report keeps 366 days, so anything older cannot be answered
                from it — and the reader can SEE that this search is older,
                because the totals carry a baseline they cannot find on the
                chart. Saying it here is the difference between a deliberate cap
                and a missing feature. Shown only when the cap is actually
                biting: a search that fits inside the window has nothing to
                explain. */}
          {cappedBefore ? (
            <p data-testid="progress-cap-notice" className="text-caption text-text-secondary">
              This report keeps the last 366 days, so the range reaches back to{" "}
              {dayLabel(bounds.first)} at most. Activity before that is folded into the totals
              rather than plotted.
            </p>
          ) : null}
        </div>
      ) : null}

      {/* CHART 1 IS A SIBLING OF CHARTS 2 AND 3, and rendered exactly once.
          It used to live inside the controls block — first as the right-hand
          column of a grid, then briefly under it — which is what made it
          narrower than the two below. Lifting it out is what actually gives the
          three "the same width" Jared asked for; anything else only makes them
          look alike at one viewport. */}
      {newInStageChart}

      {/* "Total In Each Stage" — both the wording and the casing are Jared's,
          from the 08-25 call. The longer title it
          replaces — "Cumulative entries into each stage by <bucket>" — existed to
          keep this panel from being read as "how many are in this stage now",
          which is the question the Dashboard donut answers with very different
          numbers. That distinction did not go away with the title: it is what
          `TOTAL_IN_STAGE_TOOLTIP` says, in the words he chose for it. */}
      <ReportPanel
        title="Total In Each Stage"
        tooltip={TOTAL_IN_STAGE_TOOLTIP}
        testId="stage-total"
      >
        <StageLines
          days={view.daily.days}
          series={view.cumulative}
          emptyMessage={noStages}
          testId="stage-total-lines"
          label="Total In Each Stage"
          pointLabel={pointLabel}
          pointNoun={pointNoun}
          showSummary={false}
        />
      </ReportPanel>

      <ReportPanel
        title={`Outreaches By ${pointNounTitle}`}
        tooltip={`Outreaches per ${pointNoun}, by method`}
        testId="outreach"
      >
        {/* The one footer that stays on the tab. Jared, working through the three
            of them: *"these two we definitely don't need. The bottom one is not
            bad for the total. So, maybe I would say leave this one, but get rid
            of these two."* — the survivor is `outreach-summary`, the outreach
            total over the plotted span, which is the only per-chart note that
            carries a figure rather than a restatement of the axis. It lives
            inside `OutreachLines`; the two stage charts pass `showSummary={false}`
            to drop theirs, and the two reconciliation notes that used to sit here
            (`stage-total-baseline`, `outreach-outside-count`) are gone with them.
            What those two said about the 366-day window is still said once, by
            `progress-cap-notice` above, which fires on the same condition. */}
        <OutreachLines
          days={view.outreachDays}
          emptyMessage={noOutreach}
          pointLabel={pointLabel}
          pointNoun={pointNoun}
        />
      </ReportPanel>

      {/* R6: ONE note for the whole tab, replacing the three per-chart ones.
          Jared: *"instead of this note this should be the note report generated
          date auto refreshes daily. Button to refresh on demand."*

          THE BUTTON SAYS WHO IT REFRESHES FOR. There is one cached report per
          search, so pressing this rebuilds what everyone sees — Jose said so on
          the call and Jared's answer was "that's great", so the shared effect is
          a selling point rather than a caveat to bury. "Refresh" alone would
          read as a private reload of the reader's own view, which is the one
          thing it is not. */}
      <div
        data-testid="progress-report-note"
        className="flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-border pt-3 text-caption text-text-secondary"
      >
        <span>
          {generatedLabel ? `Report generated ${generatedLabel}. ` : ""}Auto-refreshes daily.
        </span>
        {onRefresh ? (
          <Button
            variant="secondary"
            data-testid="progress-refresh"
            onClick={onRefresh}
            disabled={refreshing}
            title="Rebuilds this report from Thrive. Everyone looking at this search gets the new one."
          >
            <RefreshCw className="h-4 w-4" aria-hidden="true" />
            {refreshing ? "Refreshing…" : "Refresh for everyone"}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
